/**
 * Me · B-almanac overview.
 *
 * Layout follows design/calestea/project/miniapp-me.jsx exactly:
 *   1. Profile masthead — archetype name + identity sub-line + badges + square avatar
 *   2. Big-3 strip (Sun · Moon · Rising)
 *   3. Actions row — Share ID · Full chart · Settings
 *   4. My rites — 3-up grid of the 3 most recent purchases (skipped when empty)
 *   5. Flat row list — Star Persona · Natal Chart · Purchases · Couples · Friends · Waitlist · Settings
 *   6. Footer — Calestea · 2026
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CosmosBackdrop } from '../../components/brand/CosmosBackdrop';
import { Big3Strip } from '../../components/me/Big3Strip';
import { GiftBanner } from '../../components/me/GiftBanner';
import { useMe } from '../../hooks/useMe';
import { useChart } from '../../hooks/useChart';
import { useImageFeatures } from '../../hooks/useImageFeatures';
import { useMyPurchases } from '../../hooks/useMyPurchases';
import { useCourseState } from '../../hooks/useCourse';
import { resultRoute } from '../../lib/result-route';
import { shareLink, profileShareUrl } from '../../lib/share';
import { PurchaseTile } from '../../components/me/PurchaseTile';
import styles from '../Me.module.css';
import overviewStyles from './Overview.module.css';
import tile from '../../components/me/PurchaseTile.module.css';

function ageFromBirthDate(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  const today = new Date();
  let age = today.getUTCFullYear() - y;
  const mm = today.getUTCMonth() + 1;
  const dd = today.getUTCDate();
  if (mm < m || (mm === m && dd < d)) age -= 1;
  return age >= 0 && age < 150 ? age : null;
}

function countryFromPlace(place: string | null | undefined): string | null {
  if (!place) return null;
  const tail = place.split(',').map((s) => s.trim()).filter(Boolean).pop();
  return tail ?? null;
}


interface FlatRowProps {
  icon: string;
  label: string;
  value?: string;
  more?: string;
  onClick: () => void;
  testId?: string;
}

function FlatRow({ icon, label, value, more, onClick, testId }: FlatRowProps): JSX.Element {
  return (
    <button type="button" className={overviewStyles.flatRow} onClick={onClick} data-testid={testId}>
      <span className={overviewStyles.flatRowIcon} aria-hidden="true">{icon}</span>
      <span className={overviewStyles.flatRowLabel}>{label}</span>
      {value ? <span className={overviewStyles.flatRowValue}>{value}</span> : <span />}
      <span className={overviewStyles.flatRowMore}>{more ?? '›'}</span>
    </button>
  );
}

export function MeOverview(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation(['me', 'common', 'settings', 'course']);
  const { enabled: imagesOn } = useImageFeatures();
  const { data: me, isLoading: meLoading, isError: meError, refetch: meRefetch } = useMe();
  const { data: chart } = useChart();
  // The course row only when the course is open to this user (launched, or the owner).
  const { data: course } = useCourseState();
  const { data: purchases } = useMyPurchases(3);

  const chartReady = chart?.status === 'ready' ? chart : null;
  const archetypeLabel = chartReady?.archetype?.label ?? null;

  const firstName = me?.firstName ?? t('wandererFallback');
  const age = ageFromBirthDate(me?.birthDate);
  const country = countryFromPlace(me?.birthPlaceName);
  const profilePublic = me?.publicProfileEnabled ?? false;

  function handleShareId(): void {
    if (!me?.id || !profilePublic) return;
    // Telegram-native share (navigator.share is unavailable inside the TG webview).
    shareLink(profileShareUrl(me.id), `app.calestea.com/c/${me.id.slice(-8)}`);
  }

  return (
    <div className={styles.root}>
      <CosmosBackdrop />
      <main role="main" aria-labelledby="me-heading" className={styles.content}>

        {/* 1. PROFILE MASTHEAD — tapping it opens the natal chart */}
        <div
          className={overviewStyles.masthead}
          data-testid="me-archetype-disc"
          role="button"
          tabIndex={0}
          aria-label={t('sections.natalChart')}
          style={{ cursor: 'pointer' }}
          onClick={() => void navigate('/me/natal-chart')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              void navigate('/me/natal-chart');
            }
          }}
        >
          <div className={overviewStyles.mastheadText}>
            <div className={overviewStyles.archetypeKicker}>{t(archetypeLabel ? 'overview.archetypeKicker' : 'overview.chartKicker')}</div>
            <div className={overviewStyles.archetypeName}>
              <span
                id="me-heading"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 30,
                  fontStyle: 'italic',
                  fontWeight: 500,
                  lineHeight: 0.95,
                  letterSpacing: '-0.025em',
                  background: 'linear-gradient(135deg, var(--gold-bright), var(--amber))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
                data-testid="me-archetype-name"
              >
                {archetypeLabel ?? firstName}
              </span>
            </div>
            <div className={overviewStyles.idLines} data-testid="me-identity-line">
              {firstName}
              {age !== null && <> · {age}</>}
              {country && <> · {country}</>}
              {profilePublic && (
                <>
                  <br />
                  <span className={overviewStyles.idSlugPrefix}>app.calestea.com/c/…</span>
                  <span className={overviewStyles.idSlug}>{me?.id?.slice(-8) ?? '—'}</span>
                </>
              )}
            </div>
            {/* Badges intentionally omitted until Founder № / TG-Premium fields ship in MeResponse. */}
          </div>

          <div className={overviewStyles.mastheadDisc}>
            {meLoading ? (
              <div className={overviewStyles.discSkeleton} aria-label={t('aria.loadingAvatar')} />
            ) : (
              <div className={overviewStyles.avatarSquare} data-testid="me-masthead-avatar" aria-hidden="true">
                {me?.profileAvatarUrl ? (
                  <img src={me.profileAvatarUrl} alt="" loading="lazy" />
                ) : (
                  <span className={overviewStyles.avatarSquareInitial}>
                    {firstName.trim().charAt(0).toUpperCase() || '·'}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <GiftBanner />

        {/* 2. BIG-3 STRIP */}
        {chartReady ? (
          <Big3Strip
            sun={chartReady.sun}
            moon={chartReady.moon}
            ascendant={chartReady.ascendant}
            data-testid="me-big3-strip"
          />
        ) : (
          <div className={overviewStyles.big3Skeleton} data-testid="me-big3-strip" aria-hidden="true" />
        )}

        {/* 3. ACTIONS ROW — Share ID only. Full chart is opened by tapping the
            masthead above; Settings + Natal chart moved to the flat row list below. */}
        {profilePublic && (
          <div className={overviewStyles.actionsRow} data-testid="me-actions-row">
            <button
              type="button"
              className={overviewStyles.actionBtn}
              onClick={handleShareId}
              data-testid="me-action-share"
              aria-label={t('overview.shareId')}
            >
              <span className={overviewStyles.actionIcon}>↗</span>
              <span className={overviewStyles.actionLabel}>{t('overview.shareId')}</span>
            </button>
          </div>
        )}

        {meError && !meLoading && (
          <div className={styles.errorBlock} role="alert" data-testid="me-error">
            <p className={styles.errorMessage}>{t('overview.loadError')}</p>
            <button type="button" className={styles.retryBtn} onClick={() => void meRefetch()} data-testid="me-retry-btn">
              {t('common:actions.retry')}
            </button>
          </div>
        )}

        {/* 4. MY RITES STRIP — 3 most recent purchases, uniform tiles */}
        {imagesOn && purchases && purchases.length > 0 && (
          <section aria-labelledby="me-rites-h" data-testid="me-rites-section">
            <div className={overviewStyles.sectionHead}>
              <div className={overviewStyles.sectionHeadGroup}>
                <span className={overviewStyles.sectionTitle} id="me-rites-h">{t('overview.ritesKicker')}</span>
              </div>
              <button
                type="button"
                className={overviewStyles.sectionMore}
                onClick={() => void navigate('/me/purchases')}
                data-testid="me-rites-all"
              >
                {t('overview.all')}
              </button>
            </div>
            <div className={tile.grid} data-testid="me-rites-strip">
              {purchases.map((item, i) => (
                <PurchaseTile
                  key={item.id}
                  item={item}
                  onClick={() => void navigate(resultRoute(item.result) ?? `/sku/${item.skuCode}`)}
                  cardTestId={`me-rite-${i}`}
                />
              ))}
            </div>
          </section>
        )}

        {/* 5. FLAT MEROW LIST */}
        <section aria-label={t('aria.sections')} className={overviewStyles.flatRows} data-testid="me-section-rows">
          {/* Purchases row removed — the "все ›" link on the rites strip above
              already opens the full purchases list. */}
          {imagesOn && (
            <FlatRow icon="✦" label={t('common:brand.starPersona')} onClick={() => void navigate('/me/star-persona')} testId="me-row-star-persona" />
          )}
          <FlatRow icon="✶" label={t('me:sections.natalChart')} onClick={() => void navigate('/me/natal-chart')} testId="me-row-natal-chart" />
          <FlatRow icon="◎" label={t('me:sections.myCharts')} onClick={() => void navigate('/me/charts')} testId="me-row-my-charts" />
          <FlatRow icon="⚭" label={t('me:sections.couples')} onClick={() => void navigate('/me/couples')} testId="me-row-couples" />
          {course?.launched && (
            <FlatRow icon="☾" label={t('course:me.row')} onClick={() => void navigate('/course')} testId="me-row-course" />
          )}
          <FlatRow icon="⚙" label={t('settings:title')} onClick={() => void navigate('/settings')} testId="me-row-settings" />
        </section>

        {/* 6. FOOTER */}
        <div className={overviewStyles.footer}>{t('overview.footer', { year: new Date().getFullYear() })}</div>
      </main>
    </div>
  );
}
